import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { PlaceMapView, MapPlace } from 'components/map';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { MeetingRepository } from 'repository/meetingRepository';
import { isNativeApp, openNativePlaceExplore, openNativeGooglePlaceSearch, openExternalUrl } from 'common/webview';
import { getMapProvider } from 'common/utils/regionDetector';
import { localInputToUtc, parseTimeToMinutes } from 'common/utils/datetime';
import { useUserLocation } from 'hooks/useUserLocation';
import config from 'config/config';
import { LocationLoading } from 'components/common/LocationLoading';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import { CourseBrowseSection } from 'components/course/CourseBrowseSection';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { IcPin, IcStar, IcEdit, IcPlane, IcHotel, IcTrain, IcRestaurant, IcCafe, IcLandmark, EMOJI_ICON } from 'components/common/icons/EmojiIcons';
import {
    AppScreen, AppBar, Section, SectionHeader, Button, ButtonFooter, Chip,
    CardRail, PlaceCard, SwitchList, SwitchRow,
} from 'components/ds';
import styles from './TravelGatherPlace.module.css';
import { fetchPlacePhotos } from 'common/utils/placePhotos';

const STORAGE_KEY = 'meeting_create_data';

// 장소의 카테고리 식별자(키 문자열)를 SVG 아이콘으로 렌더 (이모지 금지).
// EMOJI_ICON 에 직접 매핑되는 키(이모지/특수문자)면 그대로 lookup,
// 카테고리 키 ('airport' | 'train' | 'hotel' | 'attraction' | 'restaurant' | 'cafe' | 'pin' 등)면 별도 매핑.
const CATEGORY_KEY_ICON: Record<string, typeof IcPin> = {
    airport: IcPlane,
    train: IcTrain,
    hotel: IcHotel,
    attraction: IcLandmark,
    restaurant: IcRestaurant,
    cafe: IcCafe,
    pin: IcPin,
};
const EmojiIc = ({ emoji, size = 16 }: { emoji?: string; size?: number }) => {
    const Comp = (emoji && (EMOJI_ICON[emoji] || CATEGORY_KEY_ICON[emoji])) || IcPin;
    return <Comp size={size} />;
};

const GATHER_TIME_SLOTS = [
    '오전 6:00', '오전 7:00', '오전 8:00', '오전 9:00', '오전 10:00',
    '오후 12:00', '오후 1:00', '오후 2:00', '오후 3:00',
];

export const TravelGatherPlace = () => {
    useSkipIfCreated();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const savedData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    // 숙박지 — 복수 등록 + 체크인/체크아웃 일자(시간 옵션)
    interface AccommodationItem {
        place: MapPlace;
        checkInDate: string;  // YYYY-MM-DD
        checkOutDate: string; // YYYY-MM-DD
        checkInTime?: string; // HH:mm
        checkOutTime?: string;// HH:mm
    }

    const [gatherTime, setGatherTime] = useState(savedData.meetingTime || '');
    const [gatherPlace, setGatherPlace] = useState<MapPlace | null>(null);
    const [accommodations, setAccommodations] = useState<AccommodationItem[]>([]);
    const [showGatherMap, setShowGatherMap] = useState(false);
    const [showAccommodationMap, setShowAccommodationMap] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [sameAsGather, setSameAsGather] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<MapPlace | null>(null);
    const [reviews, setReviews] = useState<any[]>([]);
    const [isReviewLoading, setIsReviewLoading] = useState(false);
    const [showReviews, setShowReviews] = useState(false);
    const [detailOpen, setDetailOpen] = useState(true);

    // 추천 장소
    const [gatherPlaces, setGatherPlaces] = useState<MapPlace[]>([]);
    const [accommodationPlaces, setAccommodationPlaces] = useState<MapPlace[]>([]);
    const [isGatherLoading, setIsGatherLoading] = useState(false);
    const [isAccomLoading, setIsAccomLoading] = useState(false);

    // 현재 위치 (집결장소 추천용) — useUserLocation 훅으로 캐시/스토리지 공유
    const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
    // 사용자가 지도 안에서 패닝/마커 클릭 시 갱신되는 좌표 — 추천 재로딩 기준점.
    // 초기엔 destCenter, 사용자가 이동하면 그 좌표로. PlaceSearch 의 currentLocation 과 동일 역할.
    const [anchorCenter, setAnchorCenter] = useState<{ lat: number; lng: number } | null>(null);

    const { location: cachedLocation, fetchLocation: fetchUserLocation } = useUserLocation();
    // 캐시가 있으면 로딩바 안 보임
    const isLocationLoading = !cachedLocation && !currentLocation;

    const destinationInfo = savedData.destinationInfo;
    // useMemo 로 reference 안정화 — 매 렌더 새 객체면 PlaceMapView 의 useEffect [center] 가 재실행되어
    // setCenter/setSearchHerePending 가 매 렌더 발화 → re-render 추가 트리거.
    const destCenter = useMemo(
        () => destinationInfo?.lat && destinationInfo?.lng
            ? { lat: destinationInfo.lat, lng: destinationInfo.lng } : undefined,
        [destinationInfo?.lat, destinationInfo?.lng],
    );

    // 집결지/숙박지 지도의 center prop — useMemo 로 stabilize.
    // 우선순위: selectedDetail (사용자 클릭) > anchorCenter (지도 이동) > 기본값(gatherPlace/destCenter)
    const gatherMapCenter = useMemo(
        () => gatherPlace
            ? { lat: gatherPlace.lat, lng: gatherPlace.lng }
            : (currentLocation || undefined),
        [gatherPlace?.lat, gatherPlace?.lng, currentLocation?.lat, currentLocation?.lng],
    );
    const accomMapCenter = useMemo(
        () => selectedDetail
            ? { lat: selectedDetail.lat, lng: selectedDetail.lng }
            : (anchorCenter || destCenter),
        [selectedDetail?.lat, selectedDetail?.lng, anchorCenter, destCenter],
    );

    // 현재 위치 가져오기 — 캐시 우선, 시청 fallback 제거
    useEffect(() => {
        if (cachedLocation) {
            setCurrentLocation({ lat: cachedLocation.lat, lng: cachedLocation.lng });
            fetchUserLocation().then(loc => {
                if (loc && (loc.lat !== cachedLocation.lat || loc.lng !== cachedLocation.lng)) {
                    setCurrentLocation({ lat: loc.lat, lng: loc.lng });
                }
            });
            return;
        }
        (async () => {
            const loc = await fetchUserLocation();
            if (loc) {
                setCurrentLocation({ lat: loc.lat, lng: loc.lng });
            }
            // 실패 시 currentLocation은 null 유지 — 아래 fetchGather 가 null 가드로 차단
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 집결장소 추천 (여행 유형별)
    // - 일본 여행: 김포공항, 인천공항 우선
    // - 해외 여행: 인천공항 우선
    // - 국내 여행: 여행지 인근 버스터미널, 기차역, 공항
    const travelType = destinationInfo?.type; // 'DOMESTIC' | 'OVERSEAS'
    const travelCountry = destinationInfo?.country;

    useEffect(() => {
        // currentLocation 이 들어와야 주변 음식점/카페까지 포함해 재호출
        if (!currentLocation) return;
        /* 화면을 떠나거나 위치가 바뀌어 다시 돌 때, 앞선 요청의 응답이 뒤늦게 와서
           새 목록을 덮어쓰지 않도록 막습니다. 정리 함수가 없어 느린 응답이 이긴
           적이 있습니다. */
        let cancelled = false;
        const fetchGather = async () => {
            setIsGatherLoading(true);
            const places: MapPlace[] = [];

            // 1. 여행 유형별 고정 추천 장소
            if (travelType === 'OVERSEAS') {
                // 해외 여행: 공항 추천
                const airports: MapPlace[] = [];
                if (travelCountry === '일본') {
                    airports.push(
                        { id: 'gimpo-airport', name: '김포국제공항', address: '서울 강서구 하늘길 112', lat: 37.5583, lng: 126.7906, categoryIcon: 'airport', categoryLabel: '공항' },
                        { id: 'incheon-airport', name: '인천국제공항', address: '인천 중구 공항로 272', lat: 37.4602, lng: 126.4407, categoryIcon: 'airport', categoryLabel: '공항' },
                    );
                } else {
                    airports.push(
                        { id: 'incheon-airport', name: '인천국제공항', address: '인천 중구 공항로 272', lat: 37.4602, lng: 126.4407, categoryIcon: 'airport', categoryLabel: '공항' },
                        { id: 'gimpo-airport', name: '김포국제공항', address: '서울 강서구 하늘길 112', lat: 37.5583, lng: 126.7906, categoryIcon: 'airport', categoryLabel: '공항' },
                    );
                }
                places.push(...airports);
            } else if (travelType === 'DOMESTIC' && destCenter) {
                // 국내 여행: 출발지 교통허브(서울역/김포공항)를 기본 추천으로 항상 포함
                places.push(
                    { id: 'seoul-station', name: '서울역', address: '서울 용산구 한강대로 405', lat: 37.5547, lng: 126.9707, categoryIcon: 'train', categoryLabel: 'KTX' },
                    { id: 'gimpo-airport', name: '김포국제공항', address: '서울 강서구 하늘길 112', lat: 37.5583, lng: 126.7906, categoryIcon: 'airport', categoryLabel: '공항' },
                );

                // + 여행지 인근 교통시설도 있으면 추가 (엄격 매칭 — 접미사 또는 단일 단어)
                try {
                    const res = await instance.get(getApiUrl('nearbyPlaces'), {
                        params: { lat: destCenter.lat, lng: destCenter.lng, type: 'attraction', radius: 10000, limit: 10 }
                    });
                    const data = res?.data?.data || [];
                    const isTransport = (raw: string) => {
                        const name = (raw || '').trim();
                        if (!name) return false;
                        // 한국어: 이름이 "역"/"터미널"/"공항"으로 끝나거나, 단어로 포함
                        if (/(역|터미널|공항)$/.test(name)) return true;
                        if (/\s(역|터미널|공항)(\s|$)/.test(name)) return true;
                        // 영문: 독립 단어
                        const lower = name.toLowerCase();
                        return /\b(station|terminal|airport)\b/.test(lower);
                    };
                    const added = new Set(places.map(p => p.name));
                    for (const p of data) {
                        if (!isTransport(p.name) || added.has(p.name)) continue;
                        added.add(p.name);
                        places.push({
                            id: `${p.placeId || p.name}-transport`,
                            placeId: p.placeId || undefined,
                            name: p.name,
                            address: p.address || '',
                            lat: p.lat,
                            lng: p.lng,
                            categoryIcon: 'train',
                            categoryLabel: t('travelGather.transport'),
                        });
                    }
                } catch { /* 실패 시 무시 */ }
            }

            // 2. 현위치 주변 음식점/카페 추가
            if (currentLocation) {
                try {
                    const types = ['restaurant', 'cafe'];
                    const results = await Promise.allSettled(
                        types.map(type =>
                            instance.get(getApiUrl('nearbyPlaces'), {
                                params: { lat: currentLocation.lat, lng: currentLocation.lng, type, radius: 3000, limit: 5 }
                            }).then(res => ({ type, data: res?.data?.data || [] }))
                        )
                    );
                    const addedNames = new Set(places.map(p => p.name));
                    for (const result of results) {
                        if (result.status !== 'fulfilled') continue;
                        const { type, data } = result.value;
                        for (const p of data) {
                            if (addedNames.has(p.name)) continue;
                            addedNames.add(p.name);
                            places.push({
                                id: `${p.placeId || p.name}-${type}`,
                                placeId: p.placeId || undefined,
                                name: p.name,
                                address: p.address || '',
                                lat: p.lat,
                                lng: p.lng,
                                imageUrl: p.photoUrl || undefined,
                                categoryIcon: type === 'restaurant' ? 'restaurant' : 'cafe',
                                categoryLabel: p.categoryLabel || (type === 'restaurant' ? t('travelGather.restaurant') : t('travelGather.cafe')),
                            });
                        }
                    }
                } catch { /* 실패 시 공항만 표시 */ }
            }

            if (cancelled) return;
            setGatherPlaces(places);
            setIsGatherLoading(false);

            /* 집결지 목록에도 사진을 채웁니다 — 숙박만 하고 있었습니다.
               국내 nearby(카카오)는 사진을 주지 않아서, 이 보강이 없으면
               카드가 빈 상자로 남습니다. */
            const gatherIds = places.slice(0, 6).map(p => p.placeId).filter(Boolean) as string[];
            if (gatherIds.length > 0) {
                try {
                    const photos = await fetchPlacePhotos(gatherIds);
                    if (cancelled) return;
                    if (Object.keys(photos).length > 0) {
                        setGatherPlaces(prev => prev.map(p => {
                            const pid = p.placeId || '';
                            return photos[pid] ? {
                                ...p,
                                imageUrl: photos[pid].photoUrl || p.imageUrl,
                                rating: photos[pid].rating ?? p.rating,
                            } : p;
                        }));
                    }
                } catch { /* 사진 실패 시 지도 썸네일로 떨어집니다 */ }
            }
        };
        fetchGather();
        return () => { cancelled = true; };
    }, [currentLocation?.lat, currentLocation?.lng, travelType, travelCountry]); // eslint-disable-line react-hooks/exhaustive-deps

    // 숙박/관광 추천 — anchorCenter (사용자 지도 이동) 우선, 없으면 destCenter (여행지)
    // 사용자가 지도에서 이동하면 그 좌표 근처 숙박/관광 재로딩 (PlaceSearch 와 동일 흐름)
    useEffect(() => {
        const center = anchorCenter || destCenter;
        if (!center) return;
        /* '여기서 검색' 을 잇달아 누르면 요청이 겹칩니다. 앞선 응답이 늦게 와서
           새 목록을 덮지 않도록 막습니다. */
        let cancelled = false;
        const fetchAccom = async () => {
            setIsAccomLoading(true);
            const types = ['hotel', 'attraction'];
            const results = await Promise.allSettled(
                types.map(type =>
                    instance.get(getApiUrl('nearbyPlaces'), {
                        params: { lat: center.lat, lng: center.lng, type, radius: 5000, limit: 10 }
                    }).then(res => ({ type, data: res?.data?.data || [] }))
                )
            );
            const places: MapPlace[] = [];
            const addedNames = new Set<string>();
            for (const result of results) {
                if (result.status !== 'fulfilled') continue;
                const { type, data } = result.value;
                for (const p of data) {
                    if (addedNames.has(p.name)) continue;
                    addedNames.add(p.name);
                    places.push({
                        id: `${p.placeId || p.name}-${type}`,
                        placeId: p.placeId || undefined,
                        name: p.name,
                        address: p.address || '',
                        lat: p.lat,
                        lng: p.lng,
                        imageUrl: p.photoUrl || undefined,
                        rating: p.rating || undefined,
                        reviewCount: p.userRatingsTotal || 0,
                        categoryIcon: type === 'hotel' ? 'hotel' : 'attraction',
                        categoryLabel: p.categoryLabel || (type === 'hotel' ? t('travelGather.hotel') : t('travelGather.attraction')),
                    });
                }
            }
            if (cancelled) return;
            setAccommodationPlaces(places);
            setIsAccomLoading(false);

            /* 사진 배치 요청 — 보관해 둔 원본 placeId 를 씁니다.
               예전에는 id 를 '-' 로 쪼개 앞부분을 썼는데, 카카오 placeId 자체가
               'kakao-12089662' 라 'kakao' 로 잘렸습니다. 그 값으로 부르면 빈 응답이
               와서 국내 장소 사진이 통째로 안 나왔습니다. */
            const placeIds = places.slice(0, 6).map(p => p.placeId).filter(Boolean) as string[];
            if (placeIds.length > 0) {
                try {
                    const photos = await fetchPlacePhotos(placeIds);
                    if (cancelled) return;
                    if (Object.keys(photos).length > 0) {
                        setAccommodationPlaces(prev => prev.map(p => {
                            const pid = p.placeId || '';
                            return photos[pid] ? {
                                ...p,
                                imageUrl: photos[pid].photoUrl || p.imageUrl,
                                rating: photos[pid].rating ?? p.rating
                            } : p;
                        }));
                    }
                } catch { /* 사진 실패 시 아이콘 유지 */ }
            }
        };
        fetchAccom();
        return () => { cancelled = true; };
    }, [destCenter?.lat, destCenter?.lng, anchorCenter?.lat, anchorCenter?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

    // 리뷰 조회
    const fetchReviews = async (place: MapPlace) => {
        setIsReviewLoading(true);
        setShowReviews(true);
        setReviews([]);
        try {
            const userLocale = navigator.language || 'ko';
            const res = await instance.get(getApiUrl('travelReviews'), {
                params: { name: place.name, address: place.address, lat: place.lat, lng: place.lng, limit: 5, locale: userLocale }
            });
            setReviews(res?.data?.data || []);
        } catch { setReviews([]); }
        finally { setIsReviewLoading(false); }
    };

    // 여행 시작/종료일 기본값 (YYYY-MM-DD)
    const defaultCheckIn = (savedData.travelStartDate || '').slice(0, 10);
    const defaultCheckOut = (savedData.travelEndDate || '').slice(0, 10);

    // 숙박지 추가 (중복 방지 — id 기준)
    const addAccommodation = (place: MapPlace) => {
        setAccommodations(prev => {
            if (prev.some(a => a.place.id === place.id)) return prev;
            return [...prev, {
                place,
                checkInDate: defaultCheckIn,
                checkOutDate: defaultCheckOut,
            }];
        });
    };

    // 숙박지 날짜/시간 변경
    const updateAccommodation = (idx: number, patch: Partial<AccommodationItem>) => {
        setAccommodations(prev => prev.map((a, i) => i === idx ? { ...a, ...patch } : a));
    };

    // 숙박지 제거
    const removeAccommodation = (idx: number) => {
        setAccommodations(prev => prev.filter((_, i) => i !== idx));
    };

    // 장소 카드 클릭 → 지도 + 리뷰 팝업
    const handlePlaceClick = async (place: MapPlace, mapSetter: (v: boolean) => void, placeSetter: (p: MapPlace) => void, allPlaces: MapPlace[]) => {
        const isKorea = place.lat >= 33 && place.lat <= 39 && place.lng >= 124.5 && place.lng <= 130;

        // 해외 + 네이티브 → 네이티브 PlaceExploreModal
        if (!isKorea && isNativeApp()) {
            // rev4-F · 여행 집결지 흐름은 관광 mode (여행 모임 확정)
            const result = await openNativePlaceExplore({
                places: allPlaces.filter(p => p.lat && p.lng).map(p => ({
                    id: p.id, name: p.name, address: p.address || '', lat: p.lat, lng: p.lng,
                    imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                })),
                center: destCenter || { lat: place.lat, lng: place.lng },
                title: t('travelGather.gatherTitle'),
                apiUrl: config.API_URL,
                mode: 'attraction',
            });
            if (result) {
                placeSetter({
                    id: 'native-selected', name: result.name, address: result.address,
                    lat: result.lat, lng: result.lng,
                });
            }
            return;
        }

        // 한국 또는 브라우저 → 웹뷰 지도 모드
        placeSetter(place);
        setSelectedDetail(place);
        setDetailOpen(true);
        setShowReviews(false);
        fetchReviews(place);
        setTimeout(() => mapSetter(true), 50);
    };

    // 지도 열기 (해외+네이티브 → 네이티브 Google, 그 외 → 웹뷰 카카오)
    // 판정 우선순위: 이미 선택된 집결지 → 사용자 현재 위치 → 여행지 중심(destCenter)
    // → 이 셋 다 없으면 브라우저 timezone 기반 fallback (getMapProvider 내부에서 처리)
    const openGatherMapOrNative = async () => {
        const refCoords = gatherPlace
            ? { lat: gatherPlace.lat, lng: gatherPlace.lng }
            : (currentLocation || destCenter || null);
        const useGoogle = getMapProvider(refCoords) === 'google';
        if (useGoogle && isNativeApp()) {
            // rev4-F · 집결지 선택 흐름은 여행지 안 관광 mode (숙박/관광지 특화 X · 자유 검색)
            const result = await openNativePlaceExplore({
                places: gatherPlaces.filter(p => p.lat && p.lng).map(p => ({
                    id: p.id, name: p.name, address: p.address, lat: p.lat, lng: p.lng,
                    imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                })),
                center: currentLocation || { lat: 37.5665, lng: 126.978 },
                confirmButtonText: t('travelGather.confirmGather'),
                title: t('travelGather.gatherTitle'),
                apiUrl: config.API_URL,
                mode: 'attraction',
            });
            if (result) setGatherPlace({ id: 'native', name: result.name, address: result.address, lat: result.lat, lng: result.lng });
            return;
        }
        setShowGatherMap(true);
    };

    const openAccomMapOrNative = async () => {
        /* 해외 숙박만 네이티브 구글로 넘깁니다.
           예전에는 "국내/해외 무관" 하게 항상 구글로 보냈는데, 국내에서는 카카오가
           맞습니다 — 국내 숙소 데이터도 카카오 로컬(무료)에서 옵니다. 집결지 쪽이
           이미 쓰는 getMapProvider 판정을 그대로 씁니다. */
        const accomRef = destCenter || currentLocation || null;
        if (getMapProvider(accomRef) === 'google' && isNativeApp()) {
            const center = destCenter || { lat: 37.5665, lng: 126.978 };
            const result = await openNativeGooglePlaceSearch({
                initialLocation: center,
                confirmButtonText: t('travelGather.confirmAccom'),
                title: t('travelGather.accomTitle'),
                placeholder: t('travelGather.accomPlaceTitle'),
            });
            if (result) {
                addAccommodation({
                    id: `native-${Date.now()}`,
                    name: result.name,
                    address: result.address,
                    lat: result.lat,
                    lng: result.lng,
                });
            }
            return;
        }
        setShowAccommodationMap(true);
    };

    // 숙박지 시간 검증 — 백엔드와 동일 정책 (in<out, 1시간 buffer overlap 금지).
    // 같은 묶음 안에서 여러 숙박지 입력 시 시간이 겹치는지 + 집결 시각과의 순서를 사용자에게 즉시 알림.
    const validateAccommodations = (items: AccommodationItem[]): string | null => {
        const BUFFER_MS = 60 * 60 * 1000;
        const intervals = items.map(a => {
            const ci = a.checkInDate ? new Date(`${a.checkInDate}T${(a.checkInTime || '15:00')}:00`).getTime() : NaN;
            const co = a.checkOutDate ? new Date(`${a.checkOutDate}T${(a.checkOutTime || '11:00')}:00`).getTime() : NaN;
            return { name: a.place.name, ci, co, checkInDate: a.checkInDate, checkInTime: a.checkInTime || '15:00' };
        });
        // 집결 시각 (travelStartDate + gatherTime) — 숙박지 체크인보다 앞서야 함
        const gatherStartDate = savedData.travelStartDate || '';
        /* 이 화면이 만드는 값은 "오후 6:00" 인데 예전에는 길이가 5(=HH:mm)일 때만 시각으로 쓰고
           아니면 '00:00' 으로 떨어뜨렸습니다. 길이가 7 이라 늘 자정이 되어, 아래 "집결이 체크인보다
           먼저여야 한다" 검증이 사실상 무력했습니다. 두 형식을 다 읽는 공용 파서로 바꿉니다. */
        const gatherMinutes = parseTimeToMinutes(gatherTime);
        const gatherMs = (gatherStartDate && gatherMinutes != null)
            ? new Date(`${gatherStartDate.slice(0, 10)}T00:00:00`).getTime() + gatherMinutes * 60_000
            : NaN;
        for (let i = 0; i < intervals.length; i++) {
            const a = intervals[i];
            if (!Number.isFinite(a.ci) || !Number.isFinite(a.co)) {
                return t('meetingEdit.validateMissingDate', { name: a.name });
            }
            if (a.ci >= a.co) {
                return t('meetingEdit.validateInOut', { name: a.name });
            }
            // 집결 시각 ≥ 숙박 체크인 이면 순서 위반 — 집결이 먼저 진행되어야 함
            if (Number.isFinite(gatherMs) && gatherMs >= a.ci) {
                return t('meetingEdit.validateGatherAfterHotel', {
                    name: a.name,
                    time: `${a.checkInDate?.slice(0, 10)} ${a.checkInTime}`,
                });
            }
            for (let j = 0; j < i; j++) {
                const b = intervals[j];
                if (a.ci < b.co + BUFFER_MS && b.ci - BUFFER_MS < a.co) {
                    return t('meetingEdit.validateOverlap', { a: a.name, b: b.name });
                }
            }
        }
        // 숙박지 하나라도 있는데 집결 시각 미설정
        if (intervals.length > 0 && !Number.isFinite(gatherMs)) {
            return t('meetingEdit.validateGatherMissing');
        }
        return null;
    };

    // 모임 생성
    const handleCreate = async () => {
        if (isCreating) return;
        // 숙박지 검증 — 모임 생성 전에 사용자에게 알림 (모임만 생성되고 sub_place fail 방지)
        if (!sameAsGather && accommodations.length > 0) {
            const err = validateAccommodations(accommodations);
            if (err) {
                await showAlert({ message: err });
                return;
            }
        }
        setIsCreating(true);
        try {
            const createData: any = {
                meetingName: savedData.meetingName || t('travelGather.defaultMeetingName'),
                description: savedData.description || '',
                category: 'ETC',
                meetingType: 'TRAVEL',
                travelDestination: savedData.destination || destinationInfo?.name || '',
                travelType: destinationInfo?.type === 'DOMESTIC' ? 'DOMESTIC' : 'OVERSEAS',
                travelRegionId: destinationInfo?.regionId,
                travelStartDate: savedData.travelStartDate,
                travelEndDate: savedData.travelEndDate,
                meetingTime: gatherTime,
                maxMembers: 10,
                isPublic: true,
            };
            if (gatherPlace) {
                createData.placeName = gatherPlace.name;
                createData.placeAddress = gatherPlace.address;
                createData.latitude = gatherPlace.lat;
                createData.longitude = gatherPlace.lng;
            }
            // 첫 번째 숙박지 → hotelPlaceId(하위호환). 전체는 아래에서 sub-places로 재저장
            if (accommodations.length > 0 && !sameAsGather) {
                const first = accommodations[0].place;
                createData.hotelName = first.name;
                createData.hotelAddress = first.address;
                createData.hotelLatitude = first.lat;
                createData.hotelLongitude = first.lng;
            } else if (sameAsGather && gatherPlace) {
                createData.isSameAsMeetingPlace = true;
            }
            const meetingResponse = await MeetingRepository.onPostCreateMeeting(createData);
            const resData = meetingResponse?.data?.data || meetingResponse?.data;
            const meetingId = typeof resData === 'string' ? resData : resData?.meetingId;
            const inviteCode = typeof resData === 'object' ? resData?.inviteCode : '';
            if (!meetingId) { await showAlert({ message: t('travelGather.alertCreateFailed') }); setIsCreating(false); return; }

            // 숙박지 sub-places 일괄 저장 (category=HOTEL) — 상세화면 숙박지 섹션에서 일자별로 표시됨
            if (!sameAsGather && accommodations.length > 0) {
                try {
                    // 서버는 시각을 UTC 로 받습니다
                    const toIso = (date: string, time?: string) =>
                        date ? localInputToUtc(`${date}T${time && time.length >= 4 ? time : '00:00'}`) : undefined;
                    const items = accommodations.map((a, i) => ({
                        placeName: a.place.name,
                        placeAddress: a.place.address || '',
                        latitude: typeof a.place.lat === 'number' ? a.place.lat : undefined,
                        longitude: typeof a.place.lng === 'number' ? a.place.lng : undefined,
                        category: 'HOTEL',
                        sortOrder: i + 1,
                        startDatetime: toIso(a.checkInDate, a.checkInTime),
                        endDatetime: toIso(a.checkOutDate, a.checkOutTime),
                    }));
                    await MeetingRepository.onPostCreateSubPlaces(meetingId, items);
                } catch (e) {
                    console.warn('숙박지 sub-places 저장 실패', e);
                }
            }
            const shareData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            sessionStorage.removeItem(STORAGE_KEY);
            navigate('/meeting/share', { replace: true, state: {
                meeting: { meetingId, inviteCode: inviteCode || '' },
                meetingName: shareData.meetingName || '', selectedDate: shareData.selectedDate || '',
                selectedTime: shareData.selectedTime || '', selectedPlace: shareData.selectedPlace || '',
                meetingType: shareData.meetingType || 'TRAVEL',
                travelDestination: shareData.travelDestination || '',
                travelStartDate: shareData.travelStartDate || '', travelEndDate: shareData.travelEndDate || '',
                hotelName: shareData.hotelName || shareData.hotelInfo?.name || '',
            } });
        } catch {
            await showAlert({ message: t('travelGather.alertCreateFailed') });
        } finally {
            setIsCreating(false);
        }
    };

    // 좌우 스와이프로 이전/다음 장소
    const dragStartY = useRef(0);
    const dragStartX = useRef(0);
    const dragCurrentY = useRef(0);
    const dragCurrentX = useRef(0);
    const cardElRef = useRef<HTMLDivElement>(null);
    const isDragDisabled = useRef(false);

    const switchPlace = (places: MapPlace[], direction: 'prev' | 'next', placeSetter: (p: MapPlace) => void) => {
        if (!selectedDetail) return;
        const idx = places.findIndex(p => p.id === selectedDetail.id);
        const newIdx = direction === 'next' ? idx + 1 : idx - 1;
        if (newIdx >= 0 && newIdx < places.length) {
            const newPlace = places[newIdx];
            placeSetter(newPlace);
            setSelectedDetail(newPlace);
            setShowReviews(false);
            fetchReviews(newPlace);
        }
    };

    // 리뷰 팝업 렌더링 (집결/숙박 공통)
    const renderDetailPopup = (places: MapPlace[], confirmText: string, onConfirm: () => void, placeSetter: (p: MapPlace) => void) => selectedDetail && (
        <div className={!detailOpen ? `${styles.overlay} ${styles.overlayMin}` : styles.overlay}>
            <div
                ref={cardElRef}
                className={detailOpen ? `${styles.detail} ${styles.isOpen}` : `${styles.detail} ${styles.isClosed}`}
                onTouchStart={(e) => {
                    // 리뷰처럼 스스로 스크롤하는 영역은 시트 드래그를 막습니다.
                    // 클래스로 찾으면 CSS Modules 해시 때문에 조용히 어긋나므로 data 속성으로 표시합니다.
                    const target = e.target as HTMLElement;
                    isDragDisabled.current = !!target.closest('[data-drag-lock]');
                    dragStartY.current = e.touches[0].clientY;
                    dragStartX.current = e.touches[0].clientX;
                    dragCurrentY.current = 0;
                    dragCurrentX.current = 0;
                    if (!isDragDisabled.current && cardElRef.current) cardElRef.current.style.transition = 'none';
                }}
                onTouchMove={(e) => {
                    if (isDragDisabled.current) return;
                    const diffY = e.touches[0].clientY - dragStartY.current;
                    const diffX = e.touches[0].clientX - dragStartX.current;
                    dragCurrentY.current = diffY;
                    dragCurrentX.current = diffX;
                    if (Math.abs(diffY) > Math.abs(diffX) && cardElRef.current && diffY > 0) {
                        cardElRef.current.style.transform = `translateY(${diffY}px)`;
                    }
                }}
                onTouchEnd={() => {
                    if (isDragDisabled.current) { isDragDisabled.current = false; return; }
                    if (cardElRef.current) {
                        cardElRef.current.style.transition = 'transform 0.3s ease, max-height 0.3s ease';
                        cardElRef.current.style.transform = '';
                    }
                    const absX = Math.abs(dragCurrentX.current);
                    const absY = Math.abs(dragCurrentY.current);
                    if (absX > absY && absX > 60) {
                        switchPlace(places, dragCurrentX.current < 0 ? 'next' : 'prev', placeSetter);
                    } else if (dragCurrentY.current > 80) {
                        setDetailOpen(false);
                    } else if (dragCurrentY.current < -80) {
                        setDetailOpen(true);
                    }
                }}
            >
                <div className={styles.detailHandle} onClick={() => setDetailOpen(!detailOpen)} />
                {/* 좌우 네비게이션 */}
                {(() => {
                    const currentIdx = places.findIndex(p => p.id === selectedDetail.id);
                    return (
                        <div className={styles.nav}>
                            <button className={styles.navBtn} onClick={() => switchPlace(places, 'prev', placeSetter)} disabled={currentIdx <= 0}>‹</button>
                            <span className={styles.navInfo}>{currentIdx + 1} / {places.length}</span>
                            <button className={styles.navBtn} onClick={() => switchPlace(places, 'next', placeSetter)} disabled={currentIdx >= places.length - 1}>›</button>
                        </div>
                    );
                })()}
                {!detailOpen && (
                    <div className={styles.detailMini} onClick={() => setDetailOpen(true)}>
                        <IcPin size={16} /> {selectedDetail.name}
                    </div>
                )}
                {selectedDetail.imageUrl && (
                    <div className={styles.photo}>
                        <img src={selectedDetail.imageUrl} alt={selectedDetail.name} />
                    </div>
                )}
                <div className={styles.info}>
                    <span className={styles.infoIcon}><EmojiIc emoji={selectedDetail.categoryIcon} size={18} /></span>
                    <div className={styles.infoBody}>
                        <div className={styles.name}>{selectedDetail.name}</div>
                        {selectedDetail.rating && (
                            <span className={styles.rating}><IcStar size={14} /> {selectedDetail.rating}</span>
                        )}
                        <div className={styles.addr}>{selectedDetail.address}</div>
                    </div>
                </div>
                <div className={styles.reviewsBlock}>
                    <button className={styles.reviewsToggle} onClick={() => setShowReviews(!showReviews)}>
                        <span className={styles.reviewsTitle}><IcEdit size={16} /> {t('travelGather.reviewsTitle')} {reviews.length > 0 ? `(${reviews.length})` : ''}</span>
                        <span className={styles.reviewsArrow}>{showReviews ? '▲' : '▼'}</span>
                    </button>
                    {showReviews && (
                        <div className={styles.reviewsContent} data-drag-lock="">
                            {isReviewLoading ? (
                                <div className={styles.state}>{t('travelGather.reviewsLoading')}</div>
                            ) : reviews.length === 0 ? (
                                <div className={styles.state}>{t('travelGather.reviewsEmpty')}</div>
                            ) : (
                                <div className={styles.reviews}>
                                    {reviews.map((review: any, idx: number) => (
                                        <div key={idx} className={styles.review} onClick={() => review.link && openExternalUrl(review.link)}>
                                            {review.thumbnail && (
                                                <div className={styles.reviewThumb}><img src={review.thumbnail} alt="" /></div>
                                            )}
                                            <div className={styles.reviewBody}>
                                                <div className={styles.reviewTitle}>{review.title}</div>
                                                <div className={styles.reviewDesc}>{review.description}</div>
                                                <div className={styles.reviewMeta}>
                                                    <span>{review.bloggerName}</span>
                                                    {review.postDate && <span>{review.postDate}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.confirmBtns}>
                    <button className={`${styles.confirmBtn} ${styles.confirmBtnPrimary}`} onClick={onConfirm}>{confirmText}</button>
                    <button className={`${styles.confirmBtn} ${styles.confirmBtnSecondary}`} onClick={() => setDetailOpen(false)}>{t('travelGather.collapse')}</button>
                </div>
            </div>
        </div>
    );

    // 집결지 지도 — PlaceSearch 와 동일한 full-screen wrapper + onCenterChange.
    // 사용자가 지도 이동 시 새 좌표 기준으로 추천 재로딩 (anchorCenter 갱신)
    if (showGatherMap) {
        return (
            <div className={styles.mapScreen}>
                <AppBar
                    title={t('travelGather.gatherMapTitle')}
                    mode="sub"
                    onBack={() => { setShowGatherMap(false); setSelectedDetail(null); }}
                />
                <PlaceMapView
                    places={gatherPlaces.filter(p => p.lat && p.lng)}
                    center={gatherMapCenter}
                    selectedPlaceId={gatherPlace?.id}
                    showSearch={true} showCategories={false} showBackButton={false}
                    confirmButtonText={t('travelGather.confirmGather')}
                    onPlaceSelect={(p) => { setGatherPlace(p as MapPlace); setSelectedDetail(p as MapPlace); setDetailOpen(true); fetchReviews(p as MapPlace); }}
                    onPlaceConfirm={(p) => { setGatherPlace(p); setShowGatherMap(false); setSelectedDetail(null); }}
                    onCenterChange={(c) => setAnchorCenter(c)}
                    onMapClick={() => setDetailOpen(false)}
                    detailOpen={detailOpen}
                />
                {renderDetailPopup(gatherPlaces, t('travelGather.confirmGather'), () => { setShowGatherMap(false); setSelectedDetail(null); }, setGatherPlace)}
            </div>
        );
    }

    // 숙박지 지도 — PlaceSearch 와 동일 구조
    if (showAccommodationMap) {
        return (
            <div className={styles.mapScreen}>
                <AppBar
                    title={t('travelGather.accomMapTitle')}
                    mode="sub"
                    onBack={() => { setShowAccommodationMap(false); setSelectedDetail(null); }}
                />
                <PlaceMapView
                    places={accommodationPlaces.filter(p => p.lat && p.lng)}
                    center={accomMapCenter}
                    selectedPlaceId={selectedDetail?.id}
                    showSearch={true} showCategories={false} showBackButton={false}
                    confirmButtonText={t('travelGather.confirmAccom')}
                    onPlaceSelect={(p) => { setSelectedDetail(p as MapPlace); setDetailOpen(true); fetchReviews(p as MapPlace); }}
                    onPlaceConfirm={(p) => { addAccommodation(p as MapPlace); setShowAccommodationMap(false); setSelectedDetail(null); }}
                    onCenterChange={(c) => setAnchorCenter(c)}
                    onMapClick={() => setDetailOpen(false)}
                    detailOpen={detailOpen}
                />
                {renderDetailPopup(accommodationPlaces, t('travelGather.confirmAccom'), () => {
                    if (selectedDetail) addAccommodation(selectedDetail);
                    setShowAccommodationMap(false);
                    setSelectedDetail(null);
                }, setSelectedDetail)}
            </div>
        );
    }

    /* 추천 장소 한 줄 — 집결지·숙소가 같은 모양이라 하나로 모았습니다 */
    const placeRail = (list: any[], onPick: (p: any) => void) => (
        <CardRail>
            {list.map(place => (
                <PlaceCard
                    key={place.id}
                    name={place.name}
                    imageUrl={place.imageUrl}
                    /* 사진이 없으면 그 자리의 지도가 깔립니다 */
                    at={{ lat: place.lat, lng: place.lng }}
                    /* 주소는 넣지 않습니다 — 카드 한 줄에 다 들어가지 않아 말줄임으로
                       잘리기만 하고, 고른 뒤 상세에 그대로 나옵니다. */
                    meta={[
                        place.categoryLabel || '',
                        place.rating ? `${place.rating}${place.reviewCount ? ` (${place.reviewCount})` : ''}` : '',
                    ].filter(Boolean).join(' · ')}
                    onClick={() => onPick(place)}
                />
            ))}
        </CardRail>
    );

    return (
        <AppScreen title={t('travelGather.headerTitle')} bar="sub" hasFooter>
            <LocationLoading visible={isLocationLoading} />

            {/* 사람들은 이렇게 다녀왔어요 — 여행지를 고른 직후 참고할 코스.
               코스가 없으면 섹션째 숨습니다(지금은 대부분의 지역이 그렇습니다).
               여행을 짜는 데는 지장이 없습니다 — 장소를 직접 고르는 길이
               아래에 항상 있습니다. */}
            <CourseBrowseSection regionId={destinationInfo?.regionId} />

            <Section spacing="tight">
                <MeetingProgress currentStep={4} isTravel />
                <p className={styles.lead}>
                    {savedData.destination || t('travelGather.travelDefault')}
                    {savedData.travelStartDate && savedData.travelEndDate ? (() => {
                        const s = new Date(savedData.travelStartDate);
                        const e = new Date(savedData.travelEndDate);
                        const nights = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
                        return ' · ' + t('travelGather.periodFormat', {
                            startM: s.getMonth() + 1, startD: s.getDate(),
                            endM: e.getMonth() + 1, endD: e.getDate(),
                            nights, days: nights + 1,
                        });
                    })() : ''}
                </p>
            </Section>

            {/* 집결 시간 */}
            <Section divided>
                <SectionHeader title={t('travelGather.gatherTimeTitle')} />
                <div className={styles.chips}>
                    {GATHER_TIME_SLOTS.map(time => (
                        <Chip
                            key={time}
                            label={time}
                            selected={gatherTime === time}
                            onSelect={() => setGatherTime(time)}
                        />
                    ))}
                </div>
            </Section>

            {/* 집결 장소 */}
            <Section divided>
                <SectionHeader title={t('travelGather.gatherPlaceTitle')} />
                {gatherPlace ? (
                    <div className={styles.picked}>
                        <span className={styles.pickedBody}>
                            <span className={styles.pickedName}>{gatherPlace.name}</span>
                            <span className={styles.pickedAddr}>{gatherPlace.address}</span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => setGatherPlace(null)}>
                            {t('travelGather.change')}
                        </Button>
                    </div>
                ) : (
                    <>
                        {isGatherLoading ? (
                            <p className={styles.state}>{t('travelGather.searching')}</p>
                        ) : gatherPlaces.length === 0 ? (
                            <p className={styles.state}>{t('travelGather.noNearby')}</p>
                        ) : (
                            placeRail(gatherPlaces, (place) =>
                                handlePlaceClick(place, setShowGatherMap, setGatherPlace, gatherPlaces))
                        )}
                        <Button variant="secondary" block onClick={() => openGatherMapOrNative()}>
                            {t('travelGather.selectOnMap')}
                        </Button>
                    </>
                )}
            </Section>

            {/* 숙소 */}
            <Section divided>
                <SectionHeader title={t('travelGather.accomPlaceTitle')} />

                <SwitchList>
                    <SwitchRow
                        label={t('travelGather.sameAsGather')}
                        checked={sameAsGather}
                        onChange={(next) => setSameAsGather(next)}
                    />
                </SwitchList>

                {!sameAsGather && (
                    <>
                        {accommodations.length > 0 && (
                            <ul className={styles.accoms}>
                                {accommodations.map((item, idx) => (
                                    <li key={`${item.place.id}-${idx}`} className={styles.accom}>
                                        <div className={styles.picked}>
                                            <span className={styles.pickedBody}>
                                                <span className={styles.pickedName}>{item.place.name}</span>
                                                <span className={styles.pickedAddr}>{item.place.address}</span>
                                            </span>
                                            <Button size="sm" variant="ghost" onClick={() => removeAccommodation(idx)}>
                                                {t('travelGather.deleteAccom')}
                                            </Button>
                                        </div>
                                        <div className={styles.dates}>
                                            <div className={styles.dateRow}>
                                                <span className={styles.dateLabel}>{t('travelGather.checkIn')}</span>
                                                <input
                                                    type="date"
                                                    className={styles.dateInput}
                                                    aria-label={t('travelGather.checkIn')}
                                                    value={item.checkInDate}
                                                    onChange={e => updateAccommodation(idx, { checkInDate: e.target.value })}
                                                />
                                                <input
                                                    type="time"
                                                    className={styles.dateInput}
                                                    aria-label={t('travelGather.checkIn')}
                                                    value={item.checkInTime || ''}
                                                    onChange={e => updateAccommodation(idx, { checkInTime: e.target.value })}
                                                />
                                            </div>
                                            <div className={styles.dateRow}>
                                                <span className={styles.dateLabel}>{t('travelGather.checkOut')}</span>
                                                <input
                                                    type="date"
                                                    className={styles.dateInput}
                                                    aria-label={t('travelGather.checkOut')}
                                                    value={item.checkOutDate}
                                                    onChange={e => updateAccommodation(idx, { checkOutDate: e.target.value })}
                                                />
                                                <input
                                                    type="time"
                                                    className={styles.dateInput}
                                                    aria-label={t('travelGather.checkOut')}
                                                    value={item.checkOutTime || ''}
                                                    onChange={e => updateAccommodation(idx, { checkOutTime: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {isAccomLoading ? (
                            <p className={styles.state}>{t('travelGather.searchingAccom')}</p>
                        ) : accommodationPlaces.length === 0 ? (
                            <p className={styles.state}>{t('travelGather.noAccom')}</p>
                        ) : (
                            placeRail(accommodationPlaces, async (place) => {
                                /* 해외 숙소만 네이티브 구글로 넘깁니다(그 장소 좌표를 초기 위치로,
                                   이름을 초기 검색어로). 국내는 아래 상세 시트 + 카카오 지도. */
                                if (getMapProvider({ lat: place.lat, lng: place.lng }) === 'google' && isNativeApp()) {
                                    const result = await openNativeGooglePlaceSearch({
                                        initialLocation: { lat: place.lat, lng: place.lng },
                                        initialQuery: place.name,
                                        confirmButtonText: t('travelGather.confirmAccom'),
                                        title: t('travelGather.accomTitle'),
                                        placeholder: t('travelGather.accomPlaceTitle'),
                                    });
                                    if (result) {
                                        addAccommodation({
                                            id: `native-${Date.now()}`,
                                            name: result.name,
                                            address: result.address,
                                            lat: result.lat,
                                            lng: result.lng,
                                        });
                                    }
                                    return;
                                }
                                setSelectedDetail(place);
                                setDetailOpen(true);
                                setShowReviews(false);
                                fetchReviews(place);
                                setTimeout(() => setShowAccommodationMap(true), 50);
                            })
                        )}
                        <Button variant="secondary" block onClick={() => openAccomMapOrNative()}>
                            {accommodations.length > 0
                                ? t('travelGather.selectOnMapAdd')
                                : t('travelGather.selectOnMap')}
                        </Button>
                    </>
                )}
            </Section>

            <Section divided last>
                <button type="button" className={styles.skip} onClick={() => handleCreate()}>
                    {t('travelGather.skipLater')}
                </button>
            </Section>

            <ButtonFooter>
                <Button block onClick={handleCreate} disabled={isCreating}>
                    {isCreating ? t('travelGather.creating') : t('travelGather.createBtn')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
